import { useCallback, useState } from 'react';
import '../styles/AdminPortal.css';
import { UsersTab } from './admin/UsersTab';
import { StatsTab } from './admin/StatsTab';
import { LogsTab } from './admin/LogsTab';
import { SingleIngestionTab } from './admin/SingleIngestionTab';
import { BulkIngestionTab } from './admin/BulkIngestionTab';
import { DatabaseTab } from './admin/DatabaseTab';
import { EmbeddingsTab } from './admin/EmbeddingsTab';
import { AccountsTab } from './admin/AccountsTab';

const TAB_KEYS = [
  'users',
  'statistics',
  'logs',
  'single_ingestion',
  'bulk_ingestion',
  'database',
  'embeddings',
  'accounts',
];

const TAB_LABELS = {
  users: 'Users',
  statistics: 'Statistics',
  logs: 'Logs',
  single_ingestion: 'Single Ingestion',
  bulk_ingestion: 'Bulk Ingestion',
  database: 'Database',
  embeddings: 'Embeddings',
  accounts: 'Accounts',
};

export const AdminPortal = ({
  username,
  currentUserId,
  getToken,
  onBackToChat,
  darkMode,
  toggleDarkMode,
}) => {
  const [activeTab, setActiveTab] = useState('users');
  const [usersRefreshTrigger, setUsersRefreshTrigger] = useState(0);

  const authHeaders = useCallback(() => {
    const token = getToken?.();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const refreshUsers = useCallback(() => {
    setUsersRefreshTrigger((t) => t + 1);
  }, []);

  // Each tab is mounted always; toggling display:none preserves state across
  // tab switches (form inputs, search queries, sort, etc.). Each tab gates
  // its data fetches on `visible` so initial loads stay lazy.
  const tabStyle = (key) => ({
    display: activeTab === key ? 'block' : 'none',
  });

  return (
    <div className="AdminPortal">
      <header className="ChatHeader AdminPortal-header">
        <button type="button" className="ChatButton" onClick={onBackToChat}>
          ← Back to chat
        </button>
        <div className="ChatHeader-main">
          <h1 className="ChatTitle">Admin dashboard</h1>
          <p className="ChatSubtitle">Signed in as {username}</p>
        </div>
        <div className="ThemeToggle" title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
          <span className="ThemeToggle-icon">🌙</span>
          <label className="ThemeToggle-label">
            <input
              type="checkbox"
              className="ThemeToggle-input"
              checked={!darkMode}
              onChange={toggleDarkMode}
            />
            <span className="ThemeToggle-track" />
            <span className="ThemeToggle-thumb" />
          </label>
          <span className="ThemeToggle-icon">☀️</span>
        </div>
      </header>

      <nav className="AdminPortal-tabs" aria-label="Admin sections">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={`AdminPortal-tab ${activeTab === key ? 'AdminPortal-tab--active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </nav>

      <main className="AdminPortal-body">
        <div style={tabStyle('users')}>
          <UsersTab
            visible={activeTab === 'users'}
            refreshTrigger={usersRefreshTrigger}
            currentUserId={currentUserId}
            authHeaders={authHeaders}
          />
        </div>
        <div style={tabStyle('statistics')}>
          <StatsTab
            visible={activeTab === 'statistics'}
            authHeaders={authHeaders}
          />
        </div>
        <div style={tabStyle('logs')}>
          <LogsTab
            visible={activeTab === 'logs'}
            authHeaders={authHeaders}
          />
        </div>
        <div style={tabStyle('single_ingestion')}>
          <SingleIngestionTab authHeaders={authHeaders} />
        </div>
        <div style={tabStyle('bulk_ingestion')}>
          <BulkIngestionTab authHeaders={authHeaders} />
        </div>
        <div style={tabStyle('database')}>
          <DatabaseTab
            visible={activeTab === 'database'}
            authHeaders={authHeaders}
          />
        </div>
        <div style={tabStyle('embeddings')}>
          <EmbeddingsTab
            visible={activeTab === 'embeddings'}
            authHeaders={authHeaders}
          />
        </div>
        <div style={tabStyle('accounts')}>
          <AccountsTab
            authHeaders={authHeaders}
            onAccountCreated={refreshUsers}
          />
        </div>
      </main>
    </div>
  );
};
